import type {
  IntegrationProtocol,
  IntegrationTemplate,
  TenantIntegration,
} from "@playbookai/api-clients";
import type { StatusTone } from "@playbookai/ui";
import { connectionModeLabel } from "./connection-mode.js";

/**
 * What the inventory can honestly say about one configured integration.
 *
 * EVERY VALUE HERE IS DERIVED FROM TWO LIST READS — the tenant's instances and
 * the platform's templates. That constraint is the point rather than an
 * optimisation: a supporting fact per row that costs a request per row turns a
 * list of twenty integrations into twenty-one round trips, and the first thing
 * anyone reaches for to avoid that is a placeholder. There are no placeholders
 * in this file. A fact the two reads do not carry is simply not rendered.
 *
 * Provider-blind, like everything else in this folder. `IntegrationTemplate`
 * carries the connector's own `displayName` and `protocol`; the shell renders
 * them and never learns what they mean.
 */

/**
 * The lifecycle a row reports, and the whole truthful vocabulary available from
 * list data.
 *
 * There is deliberately no "setup required" value. Distinguishing an instance
 * somebody abandoned mid-setup from one an administrator paused on purpose
 * needs a fact neither read carries — both are simply `enabled: false` — so a
 * pill claiming the difference would be inventing it. What the console CAN say
 * is that nothing runs through a not-enabled instance and that opening it
 * continues wherever its setup stopped, which is the actionable half and is
 * true of both.
 */
export type IntegrationLifecycle =
  | "enabled"
  | "not-enabled"
  | "connector-unavailable";

/**
 * Exhaustive `Record`s rather than a function with a fallback, matching the
 * convention `connection-mode.ts` sets: adding a fourth lifecycle should break
 * the build here rather than render an empty pill.
 */
export const LIFECYCLE_LABELS = {
  enabled: "Enabled",
  "not-enabled": "Not enabled",
  "connector-unavailable": "Connector unavailable",
} as const satisfies Record<IntegrationLifecycle, string>;

export const LIFECYCLE_TONES = {
  enabled: "healthy",
  "not-enabled": "neutral",
  "connector-unavailable": "critical",
} as const satisfies Record<IntegrationLifecycle, StatusTone>;

/**
 * How each lifecycle reads as a sentence, so the pill is never the only place
 * the state is stated. Colour and a two-word pill are not an explanation, and
 * "Not enabled" in particular has a consequence an administrator needs.
 */
export const LIFECYCLE_MEANINGS = {
  enabled: "Running. People with access can use it.",
  "not-enabled": "Nothing runs through it yet. Open it to continue setting up.",
  "connector-unavailable":
    "This deployment no longer registers the connector it was created from.",
} as const satisfies Record<IntegrationLifecycle, string>;

/**
 * The protocol, in the words the administrator console uses for it.
 *
 * A closed platform vocabulary, exactly like connection mode — the two values
 * belong to the contract, not to any connector — so naming them adds no
 * provider knowledge. The manifest's own `displayName` carries the rest.
 */
export const PROTOCOL_LABELS = {
  mcp: "MCP",
  rest: "REST",
} as const satisfies Record<IntegrationProtocol, string>;

/** One row of the inventory: the instance, what it was built from, and facts. */
export interface InventoryRow {
  readonly integration: TenantIntegration;
  /** Absent when the tenant holds an instance of a template no longer listed. */
  readonly template: IntegrationTemplate | undefined;
  readonly lifecycle: IntegrationLifecycle;
  /**
   * Short, real supporting facts, in reading order. Only ever built from values
   * the two reads actually returned, so an empty array renders nothing rather
   * than an em dash.
   */
  readonly facts: readonly string[];
}

/**
 * The lifecycle for one instance.
 *
 * A MISSING template is not reported as unavailable. The two reads race in
 * principle and a template the catalog did not return is a fact about the
 * catalog, not about the connector — so the honest answer falls back to what
 * the instance itself says.
 */
function lifecycleOf(
  integration: TenantIntegration,
  template: IntegrationTemplate | undefined,
): IntegrationLifecycle {
  if (template !== undefined && template.status !== "available")
    return "connector-unavailable";
  return integration.enabled ? "enabled" : "not-enabled";
}

function factsFor(
  integration: TenantIntegration,
  template: IntegrationTemplate | undefined,
): string[] {
  const facts: string[] = [];
  if (template !== undefined)
    facts.push(
      `${template.displayName} · ${PROTOCOL_LABELS[template.protocol]}`,
    );
  facts.push(connectionModeLabel(integration.connectionMode));
  if (integration.externalTenantId !== null)
    facts.push(integration.externalTenantId);
  return facts;
}

/** Join the two reads into what the list renders. Order is the server's. */
export function buildInventoryRows(
  integrations: readonly TenantIntegration[],
  templates: readonly IntegrationTemplate[],
): InventoryRow[] {
  const byId = new Map(templates.map((template) => [template.id, template]));
  return integrations.map((integration) => {
    const template = byId.get(integration.templateId);
    return {
      integration,
      template,
      lifecycle: lifecycleOf(integration, template),
      facts: factsFor(integration, template),
    };
  });
}

/**
 * Client-side search over what the row actually shows.
 *
 * Deliberately not a server query: the whole list is already in hand, it is
 * bounded by how many integrations one tenant configures, and a round trip per
 * keystroke would make a quiet filter feel like a page load. Matching is over
 * the SAME strings the row renders, so a result that matched is a result whose
 * match the reader can see.
 */
export function matchesQuery(row: InventoryRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  const haystack = [
    row.integration.displayName,
    row.integration.description ?? "",
    ...row.facts,
    LIFECYCLE_LABELS[row.lifecycle],
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

/** The status filter's value: a lifecycle, or every one of them. */
export type LifecycleFilter = IntegrationLifecycle | "all";

export function filterInventory(
  rows: readonly InventoryRow[],
  query: string,
  lifecycle: LifecycleFilter,
): InventoryRow[] {
  return rows.filter(
    (row) =>
      (lifecycle === "all" || row.lifecycle === lifecycle) &&
      matchesQuery(row, query),
  );
}
